import { BusinessObjectBase } from './base';
import { processUserInterfaceSchema } from '@schemas/business-objects/process-user-interface';
import type { ProcessUserInterfaceProperty } from './process-user-interface-property';
import type { Id } from '../base-types';

export interface ProcessUserInterfaceData {
  readonly id: Id;
  readonly name?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly processUserInterfaceProperties?: ProcessUserInterfaceProperty[];
}

export interface ProcessUserInterface extends Readonly<ProcessUserInterfaceData> {}
type ProcessUserInterfaceCreateWithChildrenData = Partial<
  Omit<ProcessUserInterfaceData, 'processUserInterfaceProperties'>
> & {
  processUserInterfaceProperties?: Partial<ProcessUserInterfaceProperty>[];
};

export class ProcessUserInterface extends BusinessObjectBase {
  static readonly entityName = 'ProcessUserInterface' as const;
  static readonly plural = 'process-user-interfaces';
  static schema = processUserInterfaceSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessUserInterface[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessUserInterface(data));
  }

  static async findOne(filter?: any): Promise<ProcessUserInterface | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessUserInterface(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessUserInterface> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessUserInterface(data);
  }

  static async create(
    data: Omit<ProcessUserInterfaceData, 'id'>,
  ): Promise<ProcessUserInterface> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessUserInterface(result);
  }

  static async createMany(
    data: Omit<ProcessUserInterfaceData, 'id'>[],
  ): Promise<ProcessUserInterface[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessUserInterface(item));
  }

  static async createWithChildren(
    data: ProcessUserInterfaceCreateWithChildrenData,
  ): Promise<ProcessUserInterface> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessUserInterface(result);
  }

  static async createManyWithChildren(
    data: ProcessUserInterfaceCreateWithChildrenData[],
  ): Promise<ProcessUserInterface[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessUserInterface(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessUserInterfaceData>,
  ): Promise<ProcessUserInterface> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessUserInterface(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
